/**
 * Boot-state fallback views.
 *
 * Shown when the tenant provider is in any non-`live` state. Three
 * design constraints:
 *
 *  1. Strings are hardcoded English fallbacks. i18n needs the tenant
 *     config to know the locale, which we don't have for `Loading` /
 *     `NotFound` / `Archived` / `Error`. The `Paused` view DOES have a
 *     config and reads from `i18n.t('tenant.paused.banner')` when the
 *     bundle has loaded; we keep a literal English fallback for the
 *     race where boot-states render before `initI18n` resolves.
 *  2. No compliance footer — legal text comes from a config we may not
 *     have.
 *  3. Visuals use only the default `:root` CSS tokens defined in
 *     `styles/global.css`. No tenant-specific styling is applied here
 *     because the brand tokens may never have been written.
 */

import type { ReactElement } from 'react';
import type { RuntimeConfig } from '@aamp/shared-types';
import { i18next } from '../../i18n/index.js';

interface ErrorProps {
  error: Error | null;
  onRetry?: () => void;
}

interface PausedProps {
  config: RuntimeConfig;
}

function BootShell({
  title,
  body,
  detail,
  action,
}: {
  title: string;
  body: string;
  detail?: string | undefined;
  action?: ReactElement | undefined;
}): ReactElement {
  return (
    <div className="boot-state" role="status" aria-live="polite">
      <h1 className="boot-state__title">{title}</h1>
      <p className="boot-state__body">{body}</p>
      {detail !== undefined && detail !== '' ? (
        <pre className="boot-state__detail">{detail}</pre>
      ) : null}
      {action ?? null}
    </div>
  );
}

export function LoadingBootState(): ReactElement {
  return <BootShell title="Loading" body="Preparing the experience." />;
}

export function PausedBootState({ config }: PausedProps): ReactElement {
  // i18n bundles ship in Phase 10. Until then, ask i18next for the key
  // and fall back to the literal English string if it returns the key
  // back (the dev-mode default for missing keys) or i18next isn't
  // initialised yet.
  const key = 'tenant.paused.banner';
  const fallback = 'Maintenance mode';
  const translated = i18next.isInitialized ? i18next.t(key) : key;
  const banner = translated === key ? fallback : String(translated);

  return (
    <BootShell
      title={banner}
      body={`${config.brand.displayName} is paused. Please check back soon.`}
    />
  );
}

export function ArchivedBootState(): ReactElement {
  return (
    <BootShell title="Site no longer available" body="This site has been retired." />
  );
}

export function NotFoundBootState(): ReactElement {
  return (
    <BootShell title="Site not found" body="No tenant configured for this host." />
  );
}

export function ErrorBootState({ error, onRetry }: ErrorProps): ReactElement {
  return (
    <BootShell
      title="Something went wrong"
      body="We couldn't load the page."
      {...(error ? { detail: `${error.name}: ${error.message}` } : {})}
      {...(onRetry
        ? {
            action: (
              <button
                type="button"
                className="boot-state__action"
                onClick={onRetry}
              >
                Try again
              </button>
            ),
          }
        : {})}
    />
  );
}
